import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { useAddDocsToDocChronology } from '@/api/mutations/useAddDocsToDocChronology';
import { useCreateDocChronology } from '@/api/mutations/useCreateDocChronology';
import { useGetDocChronologies } from '@/api/queries/useGetDocChronologies';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { cn } from '@/new/lib/utils';
import { AddToDocChronologyDialog } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/doc/components/add-to-doc-chronology-dialog';
import { CreateDocChronologyDialog } from '@/new/pages/matters/:matterId/subsets/:subsetId/chronologies/doc/components/create-doc-chronology-dialog';

interface DocChronologyDropdownProps {
  matterId: string;
  subsetId: string;
  selectedDocIds: Set<string>;
  disabled?: boolean;
  size?: 'sm' | 'default' | 'lg';
  triggerText: string;
  createDescription: string;
  addDescription: string;
}

export function DocChronologyDropdown({
  matterId,
  subsetId,
  selectedDocIds,
  disabled,
  size,
  triggerText,
  createDescription,
  addDescription,
}: DocChronologyDropdownProps) {
  const navigate = useNavigate();

  const [isAddToDocChronDialogOpen, setIsAddToDocChronDialogOpen] = useState(false);
  const [isCreateDocChronDialogOpen, setIsCreateDocChronDialogOpen] = useState(false);

  const { data: docChronologies = [], isLoading: isLoadingDocChronologies } = useGetDocChronologies({
    matterId,
    subsetId,
  });

  const createDocChronology = useCreateDocChronology({
    onSuccess: (data) => {
      navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${data.id}`);
    },
  });

  const addDocsToDocChronology = useAddDocsToDocChronology();

  const handleCreateDocChronology = (options: {
    includeKeyFacts: boolean;
    includeRelevantFacts: boolean;
    includeAllFacts: boolean;
  }) => {
    createDocChronology.mutate({
      matterId,
      subsetId,
      docIds: Array.from(selectedDocIds),
      includeKeyFacts: options.includeKeyFacts,
      includeRelevantFacts: options.includeRelevantFacts,
      includeAllFacts: options.includeAllFacts,
    });
  };

  const handleSelectDocChronology = (docChronologyId: string) => {
    addDocsToDocChronology.mutate(
      {
        matterId,
        subsetId,
        docChronologyId,
        docIds: Array.from(selectedDocIds),
      },
      {
        onSuccess: () => {
          navigate(`/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${docChronologyId}`);
        },
      },
    );
  };

  const isDisabled = disabled || selectedDocIds.size === 0 || createDocChronology.isLoading;

  return (
    <>
      <PermissionGate permission={Permission.DocChronologyCreate} uiMode="disabled">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size={size} disabled={isDisabled}>
              <Icon className="lucide--folder-git-2" />
              {triggerText}
              <Icon className="lucide--chevron-down" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className={cn(size === 'sm' && 'text-xs')}>
            <DropdownMenuItem
              className={cn(size === 'sm' && 'text-xs')}
              onClick={() => setIsCreateDocChronDialogOpen(true)}
            >
              <Icon className="lucide--plus" /> Create New
            </DropdownMenuItem>
            <DropdownMenuItem
              className={cn('pl-1.75', size === 'sm' && 'text-xs')}
              onClick={() => setIsAddToDocChronDialogOpen(true)}
            >
              <Icon className="lucide--git-branch-plus" />
              Add to Existing
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PermissionGate>

      <AddToDocChronologyDialog
        isOpen={isAddToDocChronDialogOpen}
        onOpenChange={setIsAddToDocChronDialogOpen}
        isLoadingDocChronologies={isLoadingDocChronologies}
        docChronologies={docChronologies ?? []}
        handleSelectDocChronology={handleSelectDocChronology}
        isAddingDocs={addDocsToDocChronology.isLoading}
        description={addDescription}
      />

      <CreateDocChronologyDialog
        isOpen={isCreateDocChronDialogOpen}
        onOpenChange={setIsCreateDocChronDialogOpen}
        onConfirm={handleCreateDocChronology}
        isCreating={createDocChronology.isLoading}
        description={createDescription}
      />
    </>
  );
}
